import { AxiosError } from 'axios'

/**
 * RFC 9457 `application/problem+json` as the API returns it.
 *
 * The member that matters to the client is the stable dotted error code
 * (`loans.security_deposit_insufficient`). Clients branch on that code and never on the message
 * text, which is free to be reworded, translated, or made more specific at any time.
 */
export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  /** The stable dotted code. Some responses carry it as `errorCode` instead. */
  code?: string
  errorCode?: string
  /** Field-keyed validation failures, as ASP.NET Core's validation problem produces them. */
  errors?: Record<string, string[]>
  traceId?: string
  correlationId?: string
  [extension: string]: unknown
}

export interface ApiErrorInit {
  status: number
  /** Empty when the response carried no code — the fallback chain then uses title and detail. */
  code: string
  title: string
  detail?: string
  fieldErrors?: Record<string, string[]>
  /** Ties the failure to the server's request log and audit trail when a user reports it. */
  correlationId?: string
  /** True when the request never reached the API: offline, DNS, timeout, TLS. */
  isNetworkFailure?: boolean
}

/**
 * A failure normalised into the single shape every error surface in the application consumes.
 *
 * It is a real `Error` so it carries a stack and survives every library that expects one — query
 * caches, error boundaries, the browser console — while the members that matter operationally,
 * the dotted code and the correlation id, sit on top of it.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly title: string
  readonly detail?: string
  readonly fieldErrors?: Record<string, string[]>
  readonly correlationId?: string
  readonly isNetworkFailure: boolean

  constructor(init: ApiErrorInit) {
    super(init.detail ?? init.title)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.title = init.title
    this.detail = init.detail
    this.fieldErrors = init.fieldErrors
    this.correlationId = init.correlationId
    this.isNetworkFailure = init.isNetworkFailure ?? false
  }
}

/**
 * Matches a code embedded in the problem `type` URI, for the endpoints that express it that way
 * rather than as an extension member.
 */
const CODE_IN_TYPE = /(?:urn:[\w-]+:errors?:|\/errors?\/)([a-z0-9_]+(?:\.[a-z0-9_]+)+)$/

export function isProblemDetails(value: unknown): value is ProblemDetails {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.title === 'string' ||
    typeof candidate.type === 'string' ||
    typeof candidate.status === 'number' ||
    typeof candidate.detail === 'string'
  )
}

export function extractErrorCode(problem: ProblemDetails): string {
  if (typeof problem.code === 'string' && problem.code.length > 0) {
    return problem.code
  }

  if (typeof problem.errorCode === 'string' && problem.errorCode.length > 0) {
    return problem.errorCode
  }

  const fromType = problem.type ? CODE_IN_TYPE.exec(problem.type) : null
  return fromType?.[1] ?? ''
}

const STATUS_TITLES: Record<number, string> = {
  400: 'That request could not be accepted',
  401: 'Your session has ended',
  403: 'You do not have permission for that',
  404: 'Not found',
  409: 'Someone else changed this first',
  422: 'That operation is not currently possible',
  429: 'Too many requests',
  500: 'Something went wrong on the server',
  502: 'The server is unreachable',
  503: 'The service is temporarily unavailable',
  504: 'The server took too long to respond',
}

/**
 * Normalises anything thrown by the API client — an Axios error carrying problem+json, a network
 * failure, a thrown string — into an {@link ApiError}. Callers never inspect Axios internals.
 */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error
  }

  if (error instanceof AxiosError) {
    const response = error.response

    if (!response) {
      return new ApiError({
        status: 0,
        code: 'network.unreachable',
        title: 'No connection to the server',
        detail:
          error.code === 'ECONNABORTED'
            ? 'The request timed out before the server replied.'
            : 'The request did not reach the server. Check the branch link and try again.',
        isNetworkFailure: true,
      })
    }

    const body: unknown = response.data
    if (isProblemDetails(body)) {
      return new ApiError({
        status: body.status ?? response.status,
        code: extractErrorCode(body),
        title: body.title ?? STATUS_TITLES[response.status] ?? 'Request failed',
        detail: body.detail,
        fieldErrors: body.errors,
        correlationId: body.correlationId ?? body.traceId,
      })
    }

    return new ApiError({
      status: response.status,
      code: '',
      title: STATUS_TITLES[response.status] ?? 'Request failed',
    })
  }

  if (error instanceof Error) {
    return new ApiError({
      status: 0,
      code: '',
      title: 'Something went wrong',
      detail: error.message,
    })
  }

  return new ApiError({ status: 0, code: '', title: 'Something went wrong' })
}

/** Flattens field errors for display beneath a form that has no matching field to attach them to. */
export function flattenFieldErrors(fieldErrors: Record<string, string[]> | undefined): string[] {
  if (!fieldErrors) {
    return []
  }

  return Object.entries(fieldErrors).flatMap(([field, messages]) =>
    messages.map((message) => (field && field !== '$' ? `${field}: ${message}` : message)),
  )
}
